// Cầu nối: lấy đề thi giáo viên đã lưu trong PMaker (Quản lý đề thi) → QuizQuestion[]
// để nạp vào Thẻ Trắc nghiệm của bài giảng.
import { API_CONFIG } from '../../../config/api.config'
import type { QuizQuestion } from '../../../types'

const API = API_CONFIG.python.apiUrl

export interface MyExamItem {
  id: string
  title: string
  slug: string
  is_published?: boolean
  created_at?: string | null
  attempts_count?: number
}

function token(): string {
  return (
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    ''
  )
}

// Danh sách đề GV đã lưu (cần đăng nhập PRO)
export async function fetchMyExams(): Promise<MyExamItem[]> {
  const res = await fetch(`${API}/pmaker-advanced/my-exams`, {
    headers: { Authorization: `Bearer ${token()}` },
  })
  if (!res.ok) throw new Error(`my-exams ${res.status}`)
  const data = await res.json()
  return (data?.data || []) as MyExamItem[]
}

function letterToIndex(s: any): number {
  const m = String(s ?? '').trim().toUpperCase().match(/[A-D]/)
  return m ? m[0].charCodeAt(0) - 65 : -1
}

// Map 1 câu hỏi đề thi (theo schema /exam/{slug}) → 0..n QuizQuestion
function mapExamQuestion(q: any): QuizQuestion[] {
  const t = String(q.type || '').toLowerCase()
  const lead = String(q.cau_hoi || q.question_lead || '').trim()
  const diem = Number(q.diem) || undefined

  // ── Trắc nghiệm nhiều lựa chọn ──
  if (t.includes('trac_nghiem') || q.lua_chon) {
    let raw: any[] = []
    try {
      raw = Array.isArray(q.lua_chon) ? q.lua_chon : JSON.parse(q.lua_chon || '[]')
    } catch {
      raw = []
    }
    // Đề MỚI: mỗi phương án là {id, content} — option_id ổn định. Tra đáp án đúng theo id
    // TRƯỚC (không qua letterToIndex — id là chuỗi hex ngẫu nhiên, regex /[A-D]/ có thể khớp
    // NHẦM 1 ký tự hex bên trong id, ra sai vị trí). Đề CŨ: string[] thuần, giữ cơ chế cũ.
    const isNewFormat = raw.length > 0 && typeof raw[0] === 'object' && raw[0] !== null && raw[0].id != null
    const options = raw.map((o: any) => String(isNewFormat ? (o?.content ?? '') : (o ?? '')).trim())
    let correct = isNewFormat ? raw.findIndex((o: any) => o && o.id === q.dap_an_dung) : -1
    if (correct < 0 && !isNewFormat) {
      correct = letterToIndex(q.dap_an_dung)
      if (correct < 0) correct = options.findIndex((o) => o === String(q.dap_an_dung ?? '').trim())
    }
    if (correct < 0) correct = 0
    return [{ type: 'multiple_choice', question: lead, options, correct, explanation: q.loi_giai || '', points: diem ?? 0.25 }]
  }

  // ── Đúng/Sai → mỗi mệnh đề = 1 câu true/false ──
  if (t.includes('dung_sai') || q.statement_a || q.statement_b) {
    const out: QuizQuestion[] = []
    const letters = ['a', 'b', 'c', 'd'] as const
    const present = letters.filter((l) => String(q[`statement_${l}`] ?? '').trim())
    const per = present.length ? (diem ?? 1) / present.length : diem ?? 1
    for (const l of present) {
      const stmt = String(q[`statement_${l}`]).trim()
      const ans = q[`answer_${l}`]
      out.push({
        type: 'true_false',
        question: lead ? `${lead} — ${stmt}` : stmt,
        correct: ans === true || ans === 'true' || ans === 'True' || ans === 1,
        explanation: q[`solution_${l}`] || '',
        points: per,
      })
    }
    if (out.length) return out
  }

  // ── Trả lời ngắn / tính toán ──
  const ans = String(q.dap_an_dung ?? q.answer_value ?? '').trim()
  return [{ type: 'fill_blank', question: lead, answer: ans, explanation: q.loi_giai || '', points: diem ?? 0.25 }]
}

// Lấy 1 đề theo slug → { title, questions }
export async function loadExamAsQuiz(slug: string): Promise<{ title: string; questions: QuizQuestion[] }> {
  // Gửi token: GV soạn bài là CHỦ đề → backend trả kèm đáp án để bake vào thẻ trắc nghiệm.
  // (Đề công khai ẩn đáp án với người khác; chủ đề/admin vẫn thấy.)
  const res = await fetch(`${API}/pmaker-advanced/exam/${encodeURIComponent(slug)}`, {
    headers: { Authorization: `Bearer ${token()}` },
  })
  if (!res.ok) throw new Error(`exam ${res.status}`)
  const data = (await res.json())?.data
  if (!data) throw new Error('Đề thi không tồn tại')
  const questions: QuizQuestion[] = []
  for (const part of data.parts || []) {
    for (const q of part.questions || []) {
      questions.push(...mapExamQuestion(q))
    }
  }
  return { title: data.title || 'Đề thi', questions }
}
